<script setup lang="ts">
/**
 * Días de cierre del centro, en formato listado (§22).
 *
 * El calendario alcanza para cargar un feriado cuando se lo está mirando, pero
 * no para administrar el año: no hay forma de ver todos juntos, ni de encontrar
 * los que quedaron con actividades encima. Esta pantalla es esa vista.
 *
 * Vive en Settings aunque los cierres sean de un dive center: es configuración,
 * no operación. De qué centro son lo dice el selector de la cabecera, como en
 * todo el portal: no hay filtro propio acá — tener dos formas de decir lo mismo
 * llevaba a la pantalla a mostrar un centro y al menú otro.
 */
import { computed, ref, watch } from 'vue'
import type { ClosureConflict } from '@/api/dive-centers'
import { listDiveCenterClosures, updateDiveCenterClosures } from '@/api/dive-centers'
import type { DiveCenterClosure } from '@/types'
import AlertBox from '@/components/AlertBox.vue'
import ClosuresDrawer from '@/components/ClosuresDrawer.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'

definePage({ meta: { requiresAuth: true, centerConfig: true } })

const { current, currentId, hasMultiple } = useCurrentDiveCenter()

const centerId = computed(() => currentId.value ?? '')

const closures = ref<DiveCenterClosure[]>([])
const conflicts = ref<ClosureConflict[]>([])
const loading = ref(false)
const error = ref('')

const showDrawer = ref(false)
const confirmRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)

/** Cierre que se está editando; `null` = alta. El panel gestiona uno por vez. */
const editing = ref<DiveCenterClosure | null>(null)

/**
 * Abre el panel para ese cierre.
 *
 * Se busca el ORIGINAL en `closures` en vez de pasar la fila de la tabla: las
 * filas son copias (`{ ...closure, conflict }`) y mandar la copia arrastraría
 * `conflict` al payload.
 */
function openClosure(closure: DiveCenterClosure | null) {
  editing.value = closure
    ? closures.value.find(c => c.id === closure.id) ?? closure
    : null

  showDrawer.value = true
}

/** Borrado rápido desde la fila, sin abrir el panel. */
async function removeClosure(closure: DiveCenterClosure) {
  const label = closure.from === closure.to
    ? closure.from
    : `${closure.from} → ${closure.to}`

  const ok = await confirmRef.value?.open({
    title: 'Remove this closed day?',
    message: `${label} will no longer be closed. Your schedule starts generating `
      + 'activities on those days again.',
    confirmText: 'Remove',
    confirmColor: 'error',
  })

  if (!ok)
    return

  try {
    await updateDiveCenterClosures(
      centerId.value,
      closures.value
        .filter(c => c.id !== closure.id)
        .map(({ id, ...rest }) => ({ ...rest, ...(id ? { id } : {}) })),
    )
    notifySuccess('Closed day removed.')
    await load()
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not remove the closed day.'))
  }
}

async function load() {
  if (!centerId.value)
    return

  loading.value = true
  error.value = ''
  try {
    const res = await listDiveCenterClosures(centerId.value)

    closures.value = res.closures
    conflicts.value = res.conflicts
  }
  catch (e) {
    error.value = apiMessage(e, 'Could not load the closed days.')
  }
  finally {
    loading.value = false
  }
}

/*
 * Esta ruta no lleva `:id`, así que el `:key` del `RouterView` —atado al id de
 * la ruta— no la remonta al cambiar de centro desde la cabecera. Sin este
 * watcher seguirías viendo los cierres del centro anterior.
 */
watch(centerId, load, { immediate: true })

/*
 * Filtro por año. El "todos" es explícito y no la ausencia de valor: un centro
 * con feriados de tres años cargados abre en una lista que no dice de cuándo es.
 */
const year = ref<string>(String(new Date().getFullYear()))

const years = computed(() => {
  const found = new Set<string>()

  for (const c of closures.value) {
    if (c.from)
      found.add(c.from.slice(0, 4))
    if (c.to)
      found.add(c.to.slice(0, 4))
  }

  found.add(String(new Date().getFullYear()))

  return [{ title: 'All years', value: 'all' }, ...[...found].sort().reverse().map(y => ({ title: y, value: y }))]
})

const conflictFor = (c: DiveCenterClosure) =>
  conflicts.value.find(x => x.closureId === c.id)

const rows = computed(() => {
  const filtered = year.value === 'all'
    ? closures.value
    // Un tramo que cruza el año aparece en los dos: recortarlo a uno solo lo
    // haría desaparecer del año en el que la mitad de sus días caen.
    : closures.value.filter(c => c.from.slice(0, 4) === year.value || c.to.slice(0, 4) === year.value)

  return filtered.map(c => ({ ...c, conflict: conflictFor(c) }))
})

const headers = [
  { title: 'Dates', key: 'dates' },
  { title: 'Reason', key: 'reason', sortable: false },
  { title: 'Activities', key: 'conflict', sortable: false },
  { title: '', key: 'actions', sortable: false, width: 90 },
]

/** Días que cubre el tramo, para dar idea del tamaño sin contar a mano. */
const dayCount = (c: DiveCenterClosure) => {
  const from = Date.parse(`${c.from}T00:00:00Z`)
  const to = Date.parse(`${c.to}T00:00:00Z`)

  if (!Number.isFinite(from) || !Number.isFinite(to))
    return 0

  return Math.round((to - from) / 86_400_000) + 1
}

</script>

<template>
  <div>
    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard>
      <VCardText class="d-flex flex-wrap align-center gap-4">
        <!-- De qué centro son estos cierres. Solo con varios: en una cuenta de
             uno el nombre no despeja ninguna duda. -->
        <span
          v-if="hasMultiple && current"
          class="text-body-1 font-weight-medium"
        >{{ current.name }}</span>

        <VSelect
          v-model="year"
          label="Year"
          :items="years"
          density="compact"
          style="min-inline-size: 140px; max-inline-size: 180px;"
        />

        <VSpacer />

        <VBtn
          color="primary"
          prepend-icon="ri-add-line"
          :disabled="!centerId"
          @click="openClosure(null)"
        >
          Add closed day
        </VBtn>
      </VCardText>

      <VDataTable
        :items="rows"
        item-value="id"
        :headers="headers"
        :loading="loading"
        class="text-no-wrap rounded-0"
      >
        <template #item.dates="{ item }">
          <div class="d-flex flex-column py-1">
            <span class="text-base font-weight-medium">
              {{ item.from === item.to ? item.from : `${item.from} → ${item.to}` }}
            </span>
            <span class="text-caption text-medium-emphasis">
              {{ dayCount(item) }} {{ dayCount(item) === 1 ? 'day' : 'days' }}
            </span>
          </div>
        </template>

        <template #item.reason="{ item }">
          <span :class="item.reason ? '' : 'text-disabled'">
            {{ item.reason || '—' }}
          </span>
        </template>

        <!--
          Los conflictos son el motivo de que exista esta pantalla: un día
          cerrado puede seguir teniendo actividades encima (las puntuales no las
          toca el horario, y las que tienen reservas no se borran nunca).
        -->
        <template #item.conflict="{ item }">
          <VChip
            v-if="item.conflict?.withBookings"
            color="warning"
            size="small"
            label
          >
            {{ item.conflict.withBookings }} with bookings
          </VChip>
          <VChip
            v-else-if="item.conflict?.total"
            color="secondary"
            size="small"
            label
          >
            {{ item.conflict.total }} scheduled
          </VChip>
          <span
            v-else
            class="text-disabled"
          >Clear</span>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            icon="ri-pencil-line"
            variant="text"
            size="small"
            aria-label="Open closed day"
            @click="openClosure(item)"
          />
          <VBtn
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            aria-label="Remove closed day"
            @click="removeClosure(item)"
          />
        </template>

        <template #no-data>
          <div class="py-6 text-center text-medium-emphasis">
            No closed days for this period.
          </div>
        </template>
      </VDataTable>
    </VCard>

    <!--
      El mismo panel que en Plans &amp; Bookings: cargar un cierre es el mismo
      gesto, y dos formularios distintos para lo mismo se desincronizan.
    -->
    <ClosuresDrawer
      v-model="showDrawer"
      :dive-center-id="centerId"
      :closures="closures"
      :editing="editing"
      @saved="load"
    />

    <ConfirmDialog ref="confirmRef" />

  </div>
</template>
